import { z } from 'zod';
import { surveyQuestions } from './survey.js';
/**
 * Answers are stored as normalized rows — `answers(submission_id, question_id, value JSONB)` —
 * so that dashboard queries are plain GROUP BYs over `answers` rather than JSON gymnastics
 * over per-submission blobs. `value` is a small envelope whose shape depends on the type of
 * the question being answered, so it can only be validated in the context of a definition.
 */
export const multipleChoiceAnswerSchema = z.object({ optionIds: z.array(z.string()).max(50) });
export const booleanAnswerSchema = z.object({ value: z.boolean() });
export const ratingAnswerSchema = z.object({ value: z.number().int() });
export const openTextAnswerSchema = z.object({ text: z.string() });
/** Builds the schema for one question's answer envelope, including its own constraints. */
export function answerValueSchemaFor(question) {
    switch (question.type) {
        case 'multiple_choice': {
            const validOptionIds = new Set(question.options.map((option) => option.id));
            return multipleChoiceAnswerSchema
                .refine((answer) => answer.optionIds.every((id) => validOptionIds.has(id)), {
                message: 'answer references an option that does not exist on this question',
                path: ['optionIds'],
            })
                .refine((answer) => question.allowMultiple || answer.optionIds.length <= 1, {
                message: 'this question accepts a single option',
                path: ['optionIds'],
            });
        }
        case 'boolean':
            return booleanAnswerSchema;
        case 'rating':
            return ratingAnswerSchema.refine((answer) => answer.value >= question.min && answer.value <= question.max, { message: `rating must be between ${question.min} and ${question.max}`, path: ['value'] });
        case 'open_text':
            return openTextAnswerSchema.refine((answer) => answer.text.length <= question.maxLength, {
                message: `answer must be ${question.maxLength} characters or fewer`,
                path: ['text'],
            });
    }
}
/** True when the envelope carries no actual content, e.g. an empty option list or blank text. */
function isBlank(question, value) {
    switch (question.type) {
        case 'multiple_choice':
            return value.optionIds.length === 0;
        case 'open_text':
            return value.text.trim().length === 0;
        case 'boolean':
        case 'rating':
            return false;
    }
}
/**
 * Validates a whole submission against its survey definition. Unanswered questions are
 * represented by an omitted key or null. Blank answers to optional questions are dropped
 * rather than stored, so per-question response counts stay meaningful.
 */
export function validateSubmissionAnswers(definition, submitted) {
    const errors = [];
    const answers = [];
    // Sections are presentation only, so validation runs over the flat question list.
    const questions = surveyQuestions(definition);
    const knownQuestionIds = new Set(questions.map((question) => question.id));
    for (const questionId of Object.keys(submitted)) {
        if (!knownQuestionIds.has(questionId)) {
            errors.push({ questionId, message: 'no such question on this survey' });
        }
    }
    for (const question of questions) {
        const raw = submitted[question.id];
        if (raw === undefined || raw === null) {
            if (question.required) {
                errors.push({ questionId: question.id, message: 'this question is required' });
            }
            continue;
        }
        const parsed = answerValueSchemaFor(question).safeParse(raw);
        if (!parsed.success) {
            const [firstIssue] = parsed.error.issues;
            errors.push({ questionId: question.id, message: firstIssue?.message ?? 'invalid answer' });
            continue;
        }
        if (isBlank(question, parsed.data)) {
            if (question.required) {
                errors.push({ questionId: question.id, message: 'this question is required' });
            }
            continue;
        }
        answers.push({ questionId: question.id, value: parsed.data });
    }
    return errors.length > 0 ? { ok: false, errors } : { ok: true, answers };
}
//# sourceMappingURL=answers.js.map